/**
 * ChartContainer — Replaces Recharts' ResponsiveContainer with a reliable
 * width measurement using ResizeObserver. Passes explicit pixel width/height
 * to children via render prop.
 *
 * Usage:
 *   <ChartContainer height={260}>
 *     {(width, height) => <LineChart width={width} height={height} ... />}
 *   </ChartContainer>
 */
import { useRef, useState, useEffect } from 'react';

const ChartContainer = ({ height = 260, children }) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!ref.current) return;

    // Initial measurement
    setWidth(ref.current.offsetWidth || ref.current.clientWidth || 400);

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect?.width || entry.target.offsetWidth;
        if (w > 0) setWidth(w);
      }
    });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ width: '100%', height, position: 'relative' }}>
      {width > 0 && children(width, height)}
    </div>
  );
};

export default ChartContainer;
